/**
 * Reference to an action in a scope.
 * Scopes don't contain action metadata directly - they reference actions by ID.
 *
 * @template TActionId - The type of action IDs (usually a string literal union)
 * @template TScope - The type of scope IDs (usually a string literal union)
 */
export interface ScopeActionRef<
  TActionId extends string = string,
  TScope extends string = string,
> {
  /** References an ActionDefinition.id */
  actionId: TActionId;
  /**
   * If set, this action opens this child scope instead of executing directly.
   * The presence of childScope indicates this is a submenu trigger.
   */
  childScope?: ScopeNode<TActionId, TScope>;
}

/**
 * A scope node in the tree.
 * Scopes define what actions are available in a given UI context.
 *
 * @template TActionId - The type of action IDs
 * @template TScope - The type of scope IDs
 */
export interface ScopeNode<
  TActionId extends string = string,
  TScope extends string = string,
> {
  /** Unique scope identifier */
  id: TScope;
  /** Display name for UI (e.g., "Set Priority", "Actions") */
  label?: string;
  /** Action references in this scope - order defines menu order */
  actionRefs: readonly ScopeActionRef<TActionId, TScope>[];
}

/**
 * Check if an action ref opens a child scope (is a submenu trigger).
 */
export function isSubmenuTrigger<
  TActionId extends string,
  TScope extends string,
>(
  ref: ScopeActionRef<TActionId, TScope>,
): ref is ScopeActionRef<TActionId, TScope> & {
  childScope: ScopeNode<TActionId, TScope>;
} {
  return ref.childScope !== undefined;
}

/**
 * Get all child scopes from a scope's action refs.
 */
export function getChildScopes<TActionId extends string, TScope extends string>(
  scope: ScopeNode<TActionId, TScope>,
): ScopeNode<TActionId, TScope>[] {
  return scope.actionRefs.filter(isSubmenuTrigger).map((ref) => ref.childScope);
}
